// Qualification.jsx
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

export default function Qualification() {
  const observerRef = useRef([]);

  useEffect(() => {
    // Intersection Observer for fade-in animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll(".fade-in");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="qualification-page min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      
      {/* Main Content Section */}
      <div className="content-body">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="fixed-box max-w-7xl mx-auto">
            
            {/* Two Column Layout */}
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              
              {/* Left Side - Images */}
              <div className="content-body-left lg:w-1/3 fade-in">
                <div className="space-y-6">
                  {/* Top Image */}
                  <div className="content-left-top rounded-2xl overflow-hidden shadow-2xl transform transition-all duration-500 hover:scale-105">
                    <img 
                      src="/assets/home/content_left_top.png" 
                      alt="ISO Certification"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                  
                  {/* Bottom Image */}
                  <div className="content-left-bottom rounded-2xl overflow-hidden shadow-2xl transform transition-all duration-500 hover:scale-105">
                    <img 
                      src="/assets/home/quality_left.png" 
                      alt="Quality Assurance"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Right Side - Content */}
              <div className="content-body-right lg:w-2/3 fade-in">
                {/* Logo and Title */}
                <div className="mb-8 text-center lg:text-left">
                  <div className="flex justify-center lg:justify-start mb-6">
                    <img 
                      src="/assets/logo/logo.png" 
                      alt="Anabolic Pharma Logo"
                      className="h-16 md:h-20 w-auto"
                    />
                  </div>
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight">
                    ISO gives the pharmaceutical industry new management tools
                    to improve patient safety.
                  </h3>
                </div>

                {/* Content Paragraphs */}
                <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                  <p className="fade-in">
                    The standard is of crucial importance for the pharmaceutical
                    industry and its suppliers of primary pharmaceutical packaging
                    materials. For the first time, the principles of good
                    manufacturing practice are defined within the framework of an
                    ISO standard," explains Dr. Jürgen Thürk, convenor of the
                    working group, which has developed the new standard.
                  </p>
                  
                  <p className="fade-in">
                    Good Manufacturing Practice (GMP) refers to quality control
                    and quality assurance, which enables pharmaceutical companies
                    to minimize or eliminate cases of contamination, confusion and
                    errors. This in turn protects the customer from the purchase
                    of a product that is ineffective or even dangerous. Until now,
                    however, there has been no applicable directive or rule for
                    primary packaging materials that contain, seal or be used for
                    dose application and have direct contact with the drug.
                  </p>
                  
                  <p className="fade-in text-blue-600 dark:text-blue-400 font-semibold">
                    The publication of ISO 15378: 2006, Primary Packaging
                    Materials for Drugs Special requirements
                  </p>
                  
                  <p className="fade-in">
                    For the application of ISO 9001: 2000 constitute an
                    international consensus on best practices and represent a
                    benchmark that can be used for quality improvement, training,
                    auditing and certification.
                  </p>
                  
                  <p className="fade-in">
                    Developed with the participation of pharmaceutical industry
                    experts, ISO 15378: 2006 contains, in a single document, the
                    quality management requirements of ISO 9001: 2000 together
                    with the principles of good manufacturing practice (GMP) for
                    the planning, production and delivery of primary packaging
                    materials for medicinal products.
                  </p>
                  
                  <p className="fade-in">
                    In addition, the new standard gives guidance on risk
                    management and validation and in accordance with current
                    developments worldwide, there are guidance on these two
                    aspects.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* List Box Section - Quality Features */}
      <div className="list-box bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="fixed-box list-box-list max-w-7xl mx-auto">
            
            {/* Section Header */}
            <div className="text-center mb-12 fade-in">
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                Our Quality Commitment
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              
              {/* Premium Quality Card */}
              <div className="list-box-list-item group fade-in">
                <div className="list-item-content bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 h-full transform transition-all duration-500 hover:scale-105 hover:shadow-2xl relative overflow-hidden">
                  {/* Decorative Background */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full -mr-16 -mt-16"></div>
                  
                  {/* Icon */}
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Premium Quality
                  </h3>
                  
                  <div className="space-y-3 text-gray-600 dark:text-gray-300 leading-relaxed">
                    <p>
                      Each of our products is produced in our current Good
                      Manufacturing Practice (GMP) facility to ensure optimum quality.
                      Anabolic pharma company also complies with the ISO 9001
                      standards to ensure that all aspects of our activity meet the
                      highest quality standards.
                    </p>
                    <p>
                      The quality check begins with the incoming raw material, is
                      passed on in production and ends with the delivery of the
                      finished product. Strict controls, carried out according to
                      legal requirements and standards-specific, accompany the
                      product. Through continuous quality training, our employees are
                      informed about the application of the products to be produced
                      and the quality requirements.
                    </p>
                  </div>
                  
                  <span className="list-item-tag absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></span>
                </div>
              </div>

              {/* Satisfaction Guarantee Card */}
              <div className="list-box-list-item group fade-in">
                <div className="list-item-content bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 h-full transform transition-all duration-500 hover:scale-105 hover:shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-teal-500/10 rounded-full -mr-16 -mt-16"></div>
                  
                  <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-teal-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.636 3.636a9 9 0 0112.728 0M3.636 3.636a9 9 0 010 12.728M3.636 3.636l6.364 6.364m-6.364 6.364a9 9 0 0012.728 0m-12.728 0l6.364-6.364" />
                    </svg>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    100% Satisfaction Guarantee
                  </h3>
                  
                  <div className="space-y-3 text-gray-600 dark:text-gray-300 leading-relaxed">
                    <p>
                      All products are marked in such a way as to guarantee
                      traceability over the production period, employees, tools, up to
                      the used starting materials. With each delivery of our products,
                      our customers receive a quality product, in which we commit
                      ourselves to our high quality standards.
                    </p>
                    <p>
                      From the procurement of raw materials to the packaging of the
                      finished product, our quality control team oversees every step
                      of the manufacturing process.
                    </p>
                  </div>
                  
                  <span className="list-item-tag absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-green-600 to-teal-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></span>
                </div>
              </div>

              {/* Authentication System Card */}
              <div className="list-box-list-item group fade-in">
                <div className="list-item-content bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 h-full transform transition-all duration-500 hover:scale-105 hover:shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-full -mr-16 -mt-16"></div>
                  
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-600 to-red-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    100% Authentication System Warranty
                  </h3>
                  
                  <div className="space-y-3 text-gray-600 dark:text-gray-300 leading-relaxed">
                    <p>
                      Anabolic pharma company uses only the highest quality and purity
                      raw material according to international standards.
                    </p>
                    <p>
                      All products analyzed by independent third-party laboratories to
                      verify potency and purity. Each product comes with certificate
                      of analysis.
                    </p>
                    <p>
                      Each product batch has special analysis certificates, An
                      Analysis Certificate is issued a document from quality assurance
                      that confirms that a regulated product meets its product
                      specification.
                    </p>
                  </div>
                  
                  <span className="list-item-tag absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-orange-600 to-red-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="cta-section bg-gradient-to-r from-blue-600 to-purple-600 py-16">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 fade-in">
            Ready to Experience Premium Quality?
          </h3>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto fade-in">
            Join thousands of satisfied customers who trust our certified products
          </p>
          <Link 
            to="/products" 
            className="inline-block px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 fade-in"
          >
            Browse Our Products
          </Link>
        </div>
      </div>
    </div>
  );
}